/**
 * Manual API module for the brand registry (docs/PLAN-BRAND-REGISTRY-AND-MCP-2026-08-02.md).
 *
 * Follows the same manual-path convention as ./admin-users-api.ts: the Orval client is
 * generated from a Swagger snapshot that predates the registry endpoints (and the registry
 * enrichment fields already returned by GET /brands), and regenerating it requires a running
 * API instance that isn't available here. Types below are transcribed from the real response
 * DTOs (apps/api/src/modules/registry/dto/*.ts, apps/api/src/modules/events/dto/brand*.ts).
 */
import { apiClient } from './client';

// -- Shared shapes -----------------------------------------------------------------------

export interface RegistryIdentifier {
  kind: string;
  value: string;
  sourcePlatform: string | null;
  isPrimary: boolean;
  confidence: string | null;
}

/**
 * Registry enrichment fields that GET /brands already returns alongside the legacy BrandDto
 * shape (apps/api/src/modules/events/dto/brands-response.dto.ts), but that the generated
 * BrandDto type does not declare. Cast the generated hook's data through this to get real
 * typing without duplicating the fetch (see castToEnrichedBrand below).
 */
export interface BrandRegistryEnrichment {
  id: string | null;
  slug: string | null;
  displayName: string | null;
  program: string | null;
  platform: string | null;
  status: string | null;
  identifiers: RegistryIdentifier[];
}

export interface EnrichedBrand extends BrandRegistryEnrichment {
  brand: string;
  totalEvents: number;
  lastEventAt: string | null;
  sources: string[];
}

/**
 * Normalizes the loosely-typed Orval BrandDto (which is missing the registry fields at the type
 * level, but has them at runtime -- the backend already sends them) into EnrichedBrand.
 *
 * This fills the enrichment fields explicitly rather than asserting the shape with `as`. A bare
 * cast would claim `identifiers` is always an array, so a backend that stopped sending it would
 * turn every `.map()` / `.length` on it into a runtime crash. Defaulting here keeps an
 * unenriched brand rendering exactly like one that is genuinely absent from the registry, which
 * is a state the UI already handles.
 */
export function castToEnrichedBrand(brand: unknown): EnrichedBrand {
  const raw = (brand ?? {}) as Partial<EnrichedBrand>;

  return {
    brand: raw.brand ?? '',
    totalEvents: raw.totalEvents ?? 0,
    lastEventAt: raw.lastEventAt ?? null,
    sources: raw.sources ?? [],
    id: raw.id ?? null,
    slug: raw.slug ?? null,
    displayName: raw.displayName ?? null,
    program: raw.program ?? null,
    platform: raw.platform ?? null,
    status: raw.status ?? null,
    identifiers: raw.identifiers ?? [],
  };
}

// -- GET /brands/:idOrSlug (any authenticated role, access-filtered server-side) ------------

export interface BrandCardProgram {
  id: string;
  code: string;
  name: string;
  platform: string;
  adminUrl: string | null;
  status: string;
}

export interface BrandCardEventTypeCount {
  event: string;
  count: number;
}

export interface BrandCardEvents {
  totalEvents: number;
  lastEventAt: string | null;
  sources: string[];
  byEvent: BrandCardEventTypeCount[];
}

export interface BrandCard {
  id: string;
  slug: string;
  displayName: string;
  status: string;
  program: BrandCardProgram | null;
  identifiers: RegistryIdentifier[];
  expectedEvents: string[] | null;
  dedupePolicy: unknown;
  silenceWindowHours: number | null;
  geo: string[] | null;
  defaultCurrency: string | null;
  notes: string | null;
  events: BrandCardEvents;
}

// -- /admin/registry/* (admin role only) ----------------------------------------------------

export interface PartnerProgram {
  id: string;
  code: string;
  name: string;
  platform: string;
  adminUrl: string | null;
  status: string;
  defaultCurrency: string | null;
  owner: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RegistryBrand {
  id: string;
  slug: string;
  displayName: string;
  programId: string | null;
  status: string;
  defaultCurrency: string | null;
  geo: string[] | null;
  dedupePolicy: unknown;
  expectedEvents: string[] | null;
  silenceWindowHours: number | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BrandResolutionMiss {
  id: string;
  rawBrand: string;
  source: string | null;
  samplePayload: unknown;
  hits: number;
  firstSeen: string;
  lastSeen: string;
  resolvedBrandId: string | null;
}

export interface MapMissInput {
  rawBrand: string;
  source?: string;
  brandId: string;
  createIdentifier?: boolean;
}

export const registryApi = {
  /**
   * Full registry brand card. Requires a matching registry row (404 otherwise) and is
   * access-filtered the same way as GET /brands (403 if a viewer has no access to this brand).
   */
  async getBrandCard(idOrSlug: string): Promise<BrandCard> {
    return apiClient.get<BrandCard>(`/brands/${encodeURIComponent(idOrSlug)}`);
  },

  /** Admin-only: list partner programs. */
  async listPrograms(): Promise<PartnerProgram[]> {
    return apiClient.get<PartnerProgram[]>('/admin/registry/programs');
  },

  /** Admin-only: list every registry brand (including brands with zero events), for the
   * "map to brand" picker and for computing per-program brand counts client-side. */
  async listBrands(): Promise<RegistryBrand[]> {
    return apiClient.get<RegistryBrand[]>('/admin/registry/brands');
  },

  /** Admin-only: unresolved + resolved brand_resolution_misses, sorted by hits desc server-side. */
  async listMisses(): Promise<BrandResolutionMiss[]> {
    return apiClient.get<BrandResolutionMiss[]>('/admin/registry/misses');
  },

  /** Admin-only: map an unresolved raw brand value to a registry brand. */
  async mapMiss(data: MapMissInput): Promise<BrandResolutionMiss[]> {
    return apiClient.post<BrandResolutionMiss[]>('/admin/registry/misses/map', data);
  },
};
